import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GitCompare, Trophy, Zap, Euro, Leaf, Lock, Sparkles, TrendingUp } from "lucide-react";

export default function Compare() {
  const { user, lang } = useApp();
  const isIT = lang === "it";
  const [plants, setPlants] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_pro) return;
    api.get("/plants").then(({ data }) => setPlants(data)).catch(() => {});
  }, [user?.is_pro]);

  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length < 4 ? [...s, id] : s));
    setResult(null);
  };

  const compare = async () => {
    if (selected.length < 2) { toast.error(isIT ? "Seleziona almeno 2 impianti" : "Select at least 2 plants"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/plants/compare", { plant_ids: selected });
      setResult(data);
    } catch { toast.error(isIT ? "Errore confronto" : "Compare error"); }
    finally { setLoading(false); }
  };

  if (!user?.is_pro) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
        <div className="card-surface p-12 text-center max-w-lg" data-testid="compare-locked">
          <div className="w-16 h-16 rounded-full bg-[#EAB308] flex items-center justify-center mx-auto mb-6">
            <Lock size={26} className="text-[#17301C]" />
          </div>
          <h1 className="text-3xl font-black text-[#17301C] mb-3">
            {isIT ? "Confronto Impianti · Pro" : "Plant Compare · Pro"}
          </h1>
          <p className="text-[#3A4A3E] mb-6">
            {isIT ? "Confronta fino a 4 impianti fianco a fianco: performance ratio, resa €/kWp, CO₂." : "Compare up to 4 plants side by side: performance ratio, revenue per kWp, CO₂."}
          </p>
          <Link to="/pricing" className="btn-primary" data-testid="upgrade-from-compare">
            <Sparkles size={16} /> {isIT ? "Passa a Pro" : "Upgrade to Pro"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="mb-8">
          <div className="label-eco mb-3"><GitCompare size={12} className="inline mr-1" /> {isIT ? "Multi-impianto" : "Multi-plant"}</div>
          <h1 className="text-5xl font-black tracking-tight text-[#17301C]">
            {isIT ? "Confronta gli impianti" : "Compare plants"}
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-2">
            {isIT ? "Scopri quale impianto rende di più e dove intervenire." : "Discover which plant performs best and where to act."}
          </p>
        </div>

        {plants.length < 2 ? (
          <div className="card-surface p-12 text-center">
            <p className="text-[#6E7A71] mb-4">{isIT ? "Servono almeno 2 impianti registrati per confrontare." : "At least 2 plants required to compare."}</p>
            <Link to="/dashboard" className="btn-primary" data-testid="compare-add-plants">{isIT ? "Vai alla Dashboard" : "Go to Dashboard"}</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {plants.map((p) => {
                const isSel = selected.includes(p.id);
                return (
                  <button key={p.id} onClick={() => toggle(p.id)} data-testid={`compare-select-${p.id}`}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      isSel ? "border-[#EAB308] bg-white shadow-md" : "border-[#DDE0D8] bg-white hover:border-[#17301C]"
                    }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-[#17301C]">{p.name}</div>
                      {isSel && <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-[#EAB308] text-[#17301C]">SEL</span>}
                    </div>
                    <div className="text-xs text-[#6E7A71] font-mono-nums">{p.kwp} kWp · {p.location} · {p.panels} pannelli</div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between mb-8">
              <div className="text-sm text-[#3A4A3E]">
                {isIT ? "Selezionati:" : "Selected:"} <b>{selected.length}</b> / 4
              </div>
              <button onClick={compare} disabled={loading || selected.length < 2} data-testid="compare-run" className="btn-primary disabled:opacity-50">
                {loading ? "..." : isIT ? "Confronta" : "Compare"}
              </button>
            </div>

            {result && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="compare-results">
                {/* Insight */}
                <div className="card-surface p-5 bg-[#17301C] text-white border-[#17301C]">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy size={16} className="text-[#EAB308]" />
                    <div className="label-eco text-[#EAB308]">Insight</div>
                  </div>
                  <div className="text-sm text-white/90">
                    {isIT ? "Miglior resa €/kWp:" : "Best €/kWp:"}{" "}
                    <b className="text-[#EAB308]">{result.plants.find((p) => p.plant_id === result.insight.best_revenue_per_kwp)?.name}</b>
                    {" · "}
                    {isIT ? "Miglior Performance Ratio 30gg:" : "Best 30-day PR:"}{" "}
                    <b className="text-[#EAB308]">{result.plants.find((p) => p.plant_id === result.insight.best_performance_ratio)?.name}</b>
                  </div>
                </div>

                {/* Comparison table */}
                <div className="card-surface overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#EAE7E0]">
                      <tr>
                        <th className="text-left p-4 label-eco text-[#3A4A3E]">Metrica</th>
                        {result.plants.map((p) => (
                          <th key={p.plant_id} className="text-left p-4 label-eco text-[#17301C]">{p.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "Potenza", key: "kwp", suffix: " kWp" },
                        { label: isIT ? "Città" : "City", key: "location" },
                        { label: isIT ? "Pannelli" : "Panels", key: "panels" },
                        { label: isIT ? "Batteria" : "Battery", key: "battery_kwh", suffix: " kWh" },
                        { label: isIT ? "Resa specifica" : "Yield", key: "yield_kwh_per_kwp", suffix: " kWh/kWp" },
                        { label: isIT ? "Produzione annua" : "Annual prod.", key: "annual_production_kwh", format: (v) => v.toLocaleString(), suffix: " kWh" },
                        { label: isIT ? "Risparmio annuo" : "Annual savings", key: "annual_savings_eur", format: (v) => `€ ${v.toLocaleString()}` },
                        { label: "€/kWp/anno", key: "revenue_per_kwp_eur", format: (v) => `€ ${v.toFixed(2)}` },
                        { label: "CO₂/anno", key: "co2_saved_tonnes_year", suffix: " t" },
                        { label: "Performance Ratio 30gg", key: "performance_ratio_30d_pct", suffix: " %" },
                      ].map((row) => (
                        <tr key={row.key} className="border-t border-[#DDE0D8]">
                          <td className="p-4 font-semibold text-[#3A4A3E]">{row.label}</td>
                          {result.plants.map((p) => (
                            <td key={p.plant_id} className="p-4 font-mono-nums text-[#17301C]">
                              {row.format ? row.format(p[row.key]) : p[row.key]}{row.suffix || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
